import React, { createContext, useContext, useMemo } from 'react';

import {
  TLatestPipelineExecutionsContextValue,
  TLatestPipelineExecutionsProviderProps,
} from '@/console/features/pipelines/hooks/use-latest-pipeline-executions/latest-pipeline-executions.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadLatestExecutionsForPipelinesQuery } from '@/shared/redux/api/jobs/jobs-api';
import {
  PipelineExecutionFragment,
  ReadLatestExecutionsForPipelinesQuery,
} from '@/shared/types/types.generated';

const PIPELINE_EXECUTION_POLLING_INTERVAL_MS = 30_000;

const LatestPipelineExecutionsContext =
  createContext<TLatestPipelineExecutionsContextValue>({
    isEnabled: false,
    isPendingFor: () => false,
    getExecution: () => undefined,
  });

// Fetches the newest execution for every pipeline in one batched query, replacing the
// two per-row execution fetches on the home recent-pipelines list. Polls at the batch
// level (stopping once nothing is running) so status chips stay live.
export const LatestPipelineExecutionsProvider: React.FC<
  TLatestPipelineExecutionsProviderProps
> = ({ pipelineConfigurationIds, children }) => {
  const { isJobRunning } = useJobStatus();

  const shouldStopPolling = (data: ReadLatestExecutionsForPipelinesQuery) =>
    data?.readLatestExecutionsForPipelines.every(
      (execution) => !isJobRunning(execution.status),
    ) ?? true;

  const { currentData } = usePollingQuery({
    useQueryHook: useReadLatestExecutionsForPipelinesQuery,
    queryArg: { pipelineConfigurationIds },
    queryOptions: { skip: pipelineConfigurationIds.length === 0 },
    shouldStopPolling,
    pollingInterval: PIPELINE_EXECUTION_POLLING_INTERVAL_MS,
  });

  // Keyed on contents, not identity: the id array arrives fresh every render and
  // the context value has to stay referentially stable between responses.
  const idsKey = pipelineConfigurationIds.join(',');

  const answer = useMemo(() => {
    // Nothing answered yet, so no pipeline may be reported as having no
    // executions. Read currentData, never data: `data` keeps serving the previous
    // args' result while a new set of ids is in flight, which would hand rows the
    // executions of pipelines they are not showing.
    if (!currentData) {
      return undefined;
    }

    return {
      executions: new Map<string, PipelineExecutionFragment>(
        currentData.readLatestExecutionsForPipelines.map((execution) => [
          execution.pipelineConfigurationId,
          execution,
        ]),
      ),
      // What this response covers. An id outside it is unknown rather than
      // "never ran", which is the difference between loading and "not started".
      answeredIds: new Set(pipelineConfigurationIds),
    };
  }, [currentData, idsKey]);

  const value = useMemo<TLatestPipelineExecutionsContextValue>(
    () => ({
      isEnabled: true,
      isPendingFor: (pipelineConfigurationId: string) =>
        !answer?.answeredIds.has(pipelineConfigurationId),
      getExecution: (pipelineConfigurationId: string) =>
        answer?.executions.get(pipelineConfigurationId),
    }),
    [answer],
  );

  return (
    <LatestPipelineExecutionsContext.Provider value={value}>
      {children}
    </LatestPipelineExecutionsContext.Provider>
  );
};

export const useLatestPipelineExecutionsContext = () =>
  useContext(LatestPipelineExecutionsContext);
